import { useEffect, useState } from 'react';
import { getTokenTimeRemaining } from '../utils/jwtDecoder';

/**
 * TASK-393 (edge case #1): the app has no refresh-token mechanism — a single 8h
 * access token. Once it expires the HTTP 401 interceptor hard-redirects to
 * /login, silently dropping in-progress meeting work. This hook lets a
 * banner warn the advisor to save + re-login *before* that happens.
 *
 * ponytail: client-side warning only, no silent refresh. Upgrade path if
 * sessions ever need to outlive the 8h TTL: a real backend /auth/refresh
 * endpoint + refresh-token rotation, wired into the 401 interceptor.
 */
const WARN_BEFORE_MS = 15 * 60_000; // warn within 15 min of expiry
const CHECK_INTERVAL_MS = 60_000;   // re-check every minute

export interface SessionExpiryState {
  expiringSoon: boolean;
  minutesLeft: number;
}

function computeState(): SessionExpiryState {
  const token = localStorage.getItem('auth_token');
  // remaining is 0 for missing/expired/invalid tokens — the 401 interceptor
  // owns that case, so only warn while the token is alive but nearly gone.
  const remaining = token ? getTokenTimeRemaining(token) : 0;
  const expiringSoon = remaining > 0 && remaining < WARN_BEFORE_MS;
  return { expiringSoon, minutesLeft: Math.ceil(remaining / 60_000) };
}

export function useSessionExpiryWarning(): SessionExpiryState {
  const [state, setState] = useState<SessionExpiryState>(computeState);

  useEffect(() => {
    const id = setInterval(() => setState(computeState()), CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return state;
}
